import { Editor, EditorContent } from "@tiptap/react";
import { useEffect } from "react";

interface Props {
  editor: Editor;
  onChange?: (html: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onPasteFile?: (files: File[]) => void;
}

export const EditorInput = ({
  editor,
  onChange,
  onKeyDown,
  onPasteFile,
}: Props) => {
  // Register update handler
  useEffect(() => {
    if (!editor) return;

    const handler = () => {
      const html = editor.getHTML();
      const text = editor.getText();

      // if html is <p></p>, set empty string
      if (html === "<p></p>" || !text.trim()) {
        onChange?.("");
        return;
      }

      // Remove <p></p> in last position
      const cleanedHtml = html.replace(/<p><\/p>$/g, "");
      onChange?.(cleanedHtml);
    };
    editor.on("update", handler);
    return () => {
      editor.off("update", handler);
    };
  }, [editor, onChange]);

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    if (!onPasteFile) return;
    const items = e.clipboardData?.items;
    if (!items) return;

    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file" && item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      e.preventDefault();
      onPasteFile(files);
    }
  };

  return (
    // Because it integrates with RHF, and the image doesn't need to be added to the Editor, use handlePaste in the div wrapper.
    <div onPaste={handlePaste} className="w-full">
      <EditorContent
        editor={editor}
        className="w-full max-h-[300px] overflow-y-auto py-2 break-all"
        onKeyDown={onKeyDown}
      />
    </div>
  );
};
